import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Copy, Check, ArrowLeft, Share2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const WhatsAppLinkGenerator = () => {
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [message, setMessage] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  const generateLink = () => {
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    const cleanPhone = phone.replace(/\D/g, '');
    const fullNumber = countryCode + cleanPhone;
    const encodedMessage = encodeURIComponent(message);
    
    const link = message 
      ? `https://wa.me/${fullNumber}?text=${encodedMessage}`
      : `https://wa.me/${fullNumber}`;
    
    setGeneratedLink(link);
    toast.success('Link generated!');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'WhatsApp Link',
          text: 'Chat with me on WhatsApp',
          url: generatedLink
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="whatsapp-link-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500/20 via-background to-green-500/10 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">WhatsApp Link Generator</h1>
              <p className="text-muted-foreground">Create shareable click-to-chat links</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="glass-card p-6 space-y-6">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium mb-2">Phone Number</label>
            <div className="flex gap-2">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-24 px-3 py-3 bg-white/5 border border-white/10 rounded-xl"
              >
                <option value="91">+91</option>
                <option value="1">+1</option>
                <option value="44">+44</option>
                <option value="971">+971</option>
                <option value="966">+966</option>
                <option value="65">+65</option>
                <option value="60">+60</option>
              </select>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210"
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Pre-filled Message <span className="text-muted-foreground">(Optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! I'm interested in your products..."
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
              data-testid="message-input"
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={generateLink}
            className="w-full py-4 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
            data-testid="generate-btn"
          >
            <MessageCircle className="w-5 h-5" />
            Generate WhatsApp Link
          </button>

          {/* Generated Link */}
          {generatedLink && (
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl" data-testid="generated-link-section">
              <p className="text-sm font-medium text-green-500 mb-2">Your WhatsApp Link:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  title="Copy"
                >
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
                <button
                  onClick={shareLink}
                  className="px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  title="Share"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-4 flex gap-3">
                <a
                  href={generatedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-green-500 text-white rounded-lg font-medium text-center flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Test Link
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Tips */}
        <div className="mt-8 glass-card p-6">
          <h3 className="font-semibold mb-4">Tips for Using WhatsApp Links</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Add your WhatsApp link to your website, email signature, or social media bio</li>
            <li>• Use pre-filled messages to help customers start conversations easily</li>
            <li>• Create different links for different purposes (sales, support, etc.)</li>
            <li>• Track which links generate the most conversations</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLinkGenerator;
